import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Logo from '../../assets/icons/CampusLLMLogoSmall.png';
import bgLines from "../../assets/images/bg-line1.svg";

const Setup = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [isOnPhone, setIsOnPhone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form data
  const [universityData, setUniversityData] = useState({
    handle: "",
    fullName: "",
    domain: "",
    location: "",
  });

  useEffect(() => {
    // Function to detect if user is on a phone
    const detectPhone = () => {
      const isMobileBySize = window.innerWidth < 550;
      const isMobileByAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileByTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const hasOrientationSupport = Boolean(window.DeviceOrientationEvent);
      const isPhone = isMobileBySize && (isMobileByAgent || isMobileByTouch || hasOrientationSupport);
      setIsOnPhone(isPhone);
    };

    detectPhone();
    window.addEventListener('resize', detectPhone);
    return () => window.removeEventListener('resize', detectPhone);
  }, []);

  useEffect(() => {
    // Pre-fill domain and handle from user's email
    if (currentUser && currentUser.email) {
      const domain = currentUser.email.split('@')[1];
      const handle = domain.split('.')[0].toLowerCase();
      setUniversityData(prev => ({
        ...prev,
        domain: domain,
        handle: handle
      }));
    }
  }, [currentUser]);

  // Check if user should be here (not configured university)
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      const domain = currentUser.email.split('@')[1];
      try {
        const collegeDoc = await getDoc(doc(db, 'colleges', domain));
        if (collegeDoc.exists()) {
          navigate("/");
        }
      } catch (error) {
        console.error("Error checking university:", error);
      }
    };

    checkAccess();
  }, [currentUser, navigate]);

  const handleInputChange = (field, value) => {
    setUniversityData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to update user profile with institution and role
  const updateUserProfile = async (handle, role) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);

      // Check if user document exists
      const userDoc = await getDoc(userRef);

      const userData = {
        associatedInstitution: handle,
        role: role,
        updatedAt: new Date().toISOString(),
        email: currentUser.email,
        displayName: currentUser.displayName || '',
        photoURL: currentUser.photoURL || ''
      };

      if (userDoc.exists()) {
        // Update existing user document
        await updateDoc(userRef, userData);
      } else {
        // Create new user document
        await setDoc(userRef, {
          ...userData,
          createdAt: new Date().toISOString(),
          uid: currentUser.uid
        });
      }

      console.log('User profile updated successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      // Validate required fields
      if (!universityData.fullName || !universityData.location) {
        setError("Please fill in all required fields.");
        setIsLoading(false);
        return;
      }

      // Step 1: Create university document
      const universityDoc = {
        ...universityData,
        superAdmin: currentUser.uid,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid,
        createdByEmail: currentUser.email,
        status: 'active'
      };

      await setDoc(doc(db, 'colleges', universityData.domain), universityDoc);
      console.log('University created successfully');

      // Step 2: Determine user role
      const userRole = "Super Admin"

      // Step 3: Update user profile with institution and role
      await updateUserProfile(universityData.handle, userRole);

      setSuccess("University configured and profile updated successfully! Redirecting...");

      // Small delay to show success message
      setTimeout(() => {
        navigate("/");
      }, 1500);

    } catch (error) {
      console.error("Error during setup:", error);
      setError("Failed to configure university. Please try again.");
    }

    setIsLoading(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <main className={`w-screen h-screen flex justify-center items-center relative ${isOnPhone ? "bg-black" : "bg-gradient-to-br from-[#0a0a0a] via-[#0f0b08] to-[#0a0a0a]"}`}>
      <section className={`w-full h-full flex shadow-lg overflow-hidden ${isOnPhone ? "bg-black" : "bg-transparent"}`}>
        <div className={`w-full md:w-3/5 ${isOnPhone ? "" : "border-r border-[#2a1a1e]/30"} flex flex-col p-8 backdrop-blur-md`}>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-2">
              <img className="h-12 p-1 bg-[#1a1416]/40 border border-[#3a2a2e]/50 rounded-lg backdrop-blur-sm"
                   src={Logo} alt="Logo"/>
              <div className="font-Poppins font-semibold text-3xl text-[#f5f0f1]">CampusLM</div>
            </div>
            <div className="cursor-pointer"
                 onClick={handleLogout}
            >
              {currentUser && currentUser.photoURL ? (
                  <img
                      src={currentUser.photoURL}
                      className="w-10 h-10 rounded-full object-cover border-2 border-[#B0276E]/40 backdrop-blur-sm"
                      alt={`${currentUser.displayName || 'User'}'s profile`}
                  />
              ) : (
                  <div
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-[#B0276E]/80 to-[#8a1f57]/80 backdrop-blur-sm flex items-center justify-center text-white font-medium text-sm border-2 border-[#B0276E]/40">
                    {currentUser && currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : "U"}
                  </div>
              )}
            </div>
          </div>

          <div className="flex flex-col justify-center flex-grow max-w-2xl mx-auto w-full">
            <h1 className="text-4xl font-semibold mb-4 text-[#f5f0f1]">Setup Your University</h1>
            <p className="text-lg text-[#f5f0f1]/70 mb-8">
              Configure CampusLM for your university. This is a one-time setup process.
            </p>

            {/* Display success or error message */}
            {success && (
              <div className="w-full p-3 mb-4 bg-green-500/20 border border-green-500/50 text-green-100 rounded-lg text-center backdrop-blur-sm">
                {success}
              </div>
            )}
            {error && (
                <div className="w-full p-3 mb-4 bg-red-500/20 border border-red-500/50 text-red-100 rounded-lg text-center backdrop-blur-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#f5f0f1] mb-2">
                    University Handle
                  </label>
                  <input
                    type="text"
                    value={universityData.handle}
                    className="w-full p-3 bg-[#2a1a1e]/30 border border-[#3a2a2e]/50 text-[#f5f0f1]/50 rounded-lg cursor-not-allowed backdrop-blur-sm"
                    disabled
                  />
                  <p className="text-xs text-[#f5f0f1]/50 mt-1">Auto-generated from your email domain</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#f5f0f1] mb-2">
                    Email Domain
                  </label>
                  <input
                    type="text"
                    value={universityData.domain}
                    className="w-full p-3 bg-[#2a1a1e]/30 border border-[#3a2a2e]/50 text-[#f5f0f1]/50 rounded-lg cursor-not-allowed backdrop-blur-sm"
                    disabled
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f5f0f1] mb-2">
                  Full University Name *
                </label>
                <input
                  type="text"
                  value={universityData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full p-3 bg-[#1a1416]/40 border border-[#3a2a2e]/50 text-[#f5f0f1] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B0276E]/50 backdrop-blur-sm"
                  placeholder="e.g., Harvard University"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#f5f0f1] mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  value={universityData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full p-3 bg-[#1a1416]/40 border border-[#3a2a2e]/50 text-[#f5f0f1] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B0276E]/50 backdrop-blur-sm"
                  placeholder="e.g., Cambridge, MA"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-gradient-to-r from-[#B0276E]/80 to-[#8a1f57]/80 hover:from-[#B0276E] hover:to-[#8a1f57] text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
              >
                {isLoading ? "Setting up..." : "Complete Setup"}
              </button>
            </form>
          </div>
        </div>

        <div className="items-center justify-center relative overflow-clip w-0 hidden md:flex md:w-2/5 text-white p-8 flex-col h-full">
          <div className="absolute h-full w-screen rotate-45 -mt-96 opacity-30">
            <img src={bgLines} alt="Background" />
          </div>
          <div className="absolute w-40 h-40 p-4 rounded-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-[#B0276E] via-[#F07458] to-[#FFEEA6] rounded-xl opacity-100"></div>
            <div className="absolute inset-0.5 backdrop-filter backdrop-blur-md bg-[#1a1416]/60 rounded-xl"></div>
            <div className="relative">
              <img src={Logo} alt="Logo" />
            </div>
          </div>
          <div className="absolute bottom-8 text-center max-w-md">
            <h3 className="text-2xl font-semibold mb-4 text-[#f5f0f1]">Welcome to CampusLM</h3>
            <p className="text-[#f5f0f1]/70">
              You're setting up CampusLM for your university. This configuration will enable all students and faculty to access campus services.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Setup;